import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface ContentPage {
  id: string;
  page_key: string;
  title_fr?: string;
  title_pt?: string;
  title_en?: string;
  content_fr?: string;
  content_pt?: string;
  content_en?: string;
  sections?: any;
  meta_data?: any;
  created_at: string;
  updated_at: string;
}

export const useContentPages = () => {
  const [pages, setPages] = useState<Record<string, ContentPage>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPages = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('content_pages')
        .select('*');

      if (error) throw error;

      // Convert array to object with page_key as key
      const pagesMap = (data || []).reduce((acc, page) => {
        acc[page.page_key] = page;
        return acc;
      }, {} as Record<string, ContentPage>);

      setPages(pagesMap);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  const updatePage = async (pageKey: string, updates: Partial<ContentPage>) => {
    try {
      const { data, error } = await supabase
        .from('content_pages')
        .upsert({
          page_key: pageKey,
          ...updates,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'page_key'
        })
        .select();

      if (error) throw error;

      // Update local state immediately for better UX
      if (data && data[0]) {
        setPages(prev => ({
          ...prev,
          [pageKey]: data[0]
        }));
      }
      
      // Force a complete refresh to ensure consistency
      await fetchPages();
    } catch (err) {
      throw err instanceof Error ? err : new Error('Erreur lors de la mise à jour');
    }
  };

  const getPageContent = (pageKey: string, section?: string, field?: string, defaultValue: string = '') => {
    const page = pages[pageKey];
    if (!page) return defaultValue;

    if (section && field) {
      // Handle nested properties like "step1.title"
      const keys = field.split('.');
      let value = page.sections?.[section];
      
      for (const key of keys) {
        if (value && typeof value === 'object') {
          value = value[key];
        } else {
          return defaultValue;
        }
      }
      
      return value || defaultValue;
    }

    if (section) {
      return page.sections?.[section] || defaultValue;
    }

    return page.content_fr || defaultValue;
  };

  const initializePage = async (pageKey: string, defaultContent: Partial<ContentPage>) => {
    try {
      const { error } = await supabase
        .from('content_pages')
        .upsert({
          page_key: pageKey,
          ...defaultContent
        }, {
          onConflict: 'page_key',
          ignoreDuplicates: false
        });

      if (error) throw error;
      await fetchPages();
    } catch (err) {
      console.error('Erreur lors de l\'initialisation de la page:', err);
    }
  };

  useEffect(() => {
    fetchPages();
  }, []);

  // Initialize Homepage page with default content if it doesn't exist
  useEffect(() => {
    const initializePages = async () => {
      if (!loading && pages) {
        // Initialize homepage
        if (!pages['homepage']) {
          await initializePage('homepage', {
            title_fr: 'Page d\'Accueil Roca Rio',
            sections: {
              hero: {
                main_title: "Roca Rio",
                subtitle: "par Cléa Carioca",
                tagline: "Agence de tourisme locale & engagée à Rio de Janeiro",
                description_1: "Je vous accompagne à vivre des expériences locales et immersives à Rio de Janeiro, en toute sérénité. Et parce que voyager doit avoir du sens : avec les excursions solidaires, vous soutenez l'association Meninos do Brasil, qui aide les communautés locales dans le besoin.",
                description_2: "\"Ensemble, on découvre Rio de Janeiro de façon locale et solidaire.\"",
                cta_primary: "Je réserve mon excursion",
                cta_secondary: "Découvrir les excursions"
              }
            }
          });
        }

        // Initialize about page
        if (!pages['about']) {
          await initializePage('about', {
            title_fr: 'À Propos - Roca Rio',
            sections: {
              hero: {
                title: "Derrière Roca Rio",
                subtitle: "L'histoire de Cléa Carioca et sa passion pour un tourisme authentique et solidaire",
                cta_text: "Découvrir mon histoire"
              },
              story: {
                title: "Mon Histoire",
                subtitle: "De la France au Brésil, découvrez le parcours qui m'a menée à créer Roca Rio",
                step1: {
                  title: "La Découverte",
                  quote: "Mon histoire d'amour avec le Brésil a commencé bien avant mon installation à Rio...",
                  description: "Française passionnée de voyage, j'ai d'abord découvert ce pays extraordinaire en tant que voyageuse curieuse de nouvelles cultures."
                },
                step2: {
                  title: "L'Installation",
                  quote: "C'est en m'installant à Rio que j'ai vraiment compris la richesse de cette ville...",
                  description: "Au-delà des paysages à couper le souffle, j'ai découvert les contrastes sociaux qui invitent à la réflexion et à l'action solidaire."
                },
                step3: {
                  title: "La Mission",
                  quote: "Créer Roca Rio, c'était concrétiser un rêve : partager ma passion pour Rio tout en ayant un impact positif sur les communautés locales.",
                  description: "Un tourisme différent, plus respectueux et authentique, loin des sentiers battus traditionnels."
                }
              },
              meninos: {
                title: 'Une association, Meninos do Brasil',
                subtitle: 'Une aventure avec du sens',
                paragraph1: 'Mon engagement ne s\'arrête pas aux visites. Depuis 2018, j\'ai créé l\'association <strong>Meninos do Brasil</strong>, qui aide les personnes les plus vulnérables au Brésil.',
                paragraph2: 'Certaines de mes excursions sont des <strong>visites à impact solidaire</strong> : une partie des revenus est reversée à l\'association pour financer la distribution de paniers alimentaires, de matériel scolaire et de vêtements, ainsi que des activités éducatives, artistiques et sportives pour les enfants.',
                paragraph3: 'Plusieurs fois par an, j\'organise également des <strong>randonnées solidaires</strong>, dont l\'intégralité des fonds est reversée à Meninos do Brasil. Si vous êtes à Rio pendant ces événements, vous êtes bien sûr invités à y participer.',
                paragraph4: 'En quelques années, <strong>plus de 500 personnes</strong> ont déjà bénéficié de notre aide dans <strong>5 États différents du Brésil</strong>.',
                paragraph5: 'Avec Roca Rio, vous ne faites pas qu\'explorer la ville : vous contribuez à un voyage porteur de sens, et vos réservations solidaires participent directement à cette aventure.'
              },
              mission: {
                title: "Mission Roca Rio",
                subtitle: "Un tourisme authentique qui crée de la valeur pour tous",
                values: {
                  authenticity: {
                    title: "Authenticité",
                    description: "Découvrir Rio comme les locaux, loin des circuits touristiques traditionnels"
                  },
                  solidarity: {
                    title: "Solidarité", 
                    description: "Chaque excursion contribue au développement des communautés locales"
                  },
                  excellence: {
                    title: "Excellence",
                    description: "Des expériences inoubliables avec une guide passionnée et expérimentée"
                  }
                }
              },
              impact_statistics: {
                title: 'Notre Impact Solidaire',
                subtitle: 'Ensemble, nous créons un impact positif durable',
                stat1_value: '150+',
                stat1_label: 'Voyageurs solidaires',
                stat2_value: '€3,500',
                stat2_label: 'Collectés pour l\'association',
                stat3_value: '25',
                stat3_label: 'Familles aidées',
                stat4_value: '3',
                stat4_label: 'Projets éducatifs',
                impact1_title: 'Éducation',
                impact1_description: 'Soutien aux écoles et programmes éducatifs dans les communautés',
                impact2_title: 'Environnement',
                impact2_description: 'Projets de préservation et de reforestation urbaine',
                impact3_title: 'Social',
                impact3_description: 'Aide aux familles et développement communautaire',
                impact4_title: 'Culture',
                impact4_description: 'Promotion de l\'art et de la culture locale'
              },
              testimonials_section: {
                title: 'Ce que disent mes voyageurs',
                subtitle: 'Leurs expériences authentiques avec Roca Rio',
                button_text: 'Voir tous les avis'
              },
              cta: {
                title: 'Prêt(e) pour une aventure authentique ?',
                description: 'Découvrez Rio autrement et participez à un tourisme qui a du sens. Chaque excursion est une nouvelle histoire à écrire ensemble.',
                primary_button: 'Découvrir nos excursions',
                secondary_button: 'Nous contacter'
              }
            }
          });
        }

        // Initialize mentions-legales
        if (!pages['mentions-legales']) {
          await initializePage('mentions-legales', {
            title_fr: 'Mentions Légales',
            sections: {
              header: {
                title: "Mentions Légales",
                subtitle: "Informations légales relatives au site Roca Rio"
              },
              editor: {
                title: "Éditeur du site",
                company_name: "Roca Rio",
                responsible: "Cléa Le Chanjour",
                contact: "contact.rocario@gmail.com",
                phone: "+55 21 97679 5050",
                address: "Rio de Janeiro, Brésil",
                cnpj: "62.527.088/0001-51",
                guide_number: "19.929266.64-1"
              },
              hosting: {
                title: "Hébergement",
                provider: "Lovable",
                address: "2261 Market Street #4321, San Francisco, CA 94114, États-Unis."
              },
              activity: {
                title: "Activité",
                description: "Roca Rio est une agence de tourisme locale et engagée à Rio de Janeiro, proposant des excursions, transferts et services d'accompagnement."
              },
              intellectual_property: {
                title: "Propriété intellectuelle",
                content: "L'ensemble du contenu du site (textes, images, graphismes, logo, vidéos) est protégé par le droit d'auteur. Toute reproduction ou utilisation sans autorisation est interdite."
              },
              responsibility: {
                title: "Responsabilité",
                content: "Les informations diffusées sur ce site sont fournies à titre indicatif. Roca Rio ne saurait être tenue responsable d'erreurs ou omissions."
              }
            }
          });
        }

        // Initialize politique-confidentialite
        if (!pages['politique-confidentialite']) {
          await initializePage('politique-confidentialite', {
            title_fr: 'Politique de Confidentialité',
            sections: {
              header: {
                title: "Politique de Confidentialité",
                subtitle: "Comment nous protégeons et utilisons vos données personnelles"
              },
              data_collection: {
                title: "Collecte des données",
                intro: "Nous collectons uniquement les informations nécessaires pour :",
                purposes: "<ul><li>Répondre à vos demandes via le formulaire de contact ou par WhatsApp</li><li>Gérer vos réservations d'excursions</li><li>Vous transmettre des informations utiles (ex : bons plans, actualités)</li></ul>"
              },
              data_types: {
                title: "Types de données collectées",
                types: "<ul><li><strong>Nom, prénom</strong></li><li><strong>Adresse e-mail</strong></li><li><strong>Numéro de téléphone</strong></li><li><strong>Informations liées à la réservation</strong> (date, excursion choisie)</li></ul>"
              },
              data_usage: {
                title: "Utilisation des données",
                content: "Vos données ne sont utilisées qu'à des fins de gestion des excursions et de communication avec vous. Elles ne sont en aucun cas revendues à des tiers."
              },
              data_retention: {
                title: "Durée de conservation",
                content: "Les données sont conservées pendant la durée nécessaire à la relation commerciale, et jusqu'à 3 ans après le dernier contact."
              },
              data_security: {
                title: "Sécurité des données",
                content: "Nous mettons en place des mesures techniques pour protéger vos données contre tout accès non autorisé."
              },
              user_rights: {
                title: "Vos droits",
                content: "Conformément au RGPD, vous disposez d'un droit d'accès, de rectification et de suppression de vos données.",
                contact_info: "contact.rocario@gmail.com"
              },
              cookies: {
                title: "Cookies et Outils d'Analyse",
                content: "<p>Nous utilisons différents types de cookies :</p><ul><li><strong>Cookies essentiels :</strong> Nécessaires au fonctionnement du site (navigation, sécurité)</li><li><strong>Cookies analytiques :</strong> Google Analytics pour comprendre l'utilisation du site</li><li><strong>Cookies marketing :</strong> Personnalisation des contenus et mesure publicitaire</li></ul><p>Vous pouvez modifier vos préférences à tout moment via le lien \"Paramètres des cookies\" en bas de page.</p>"
              }
            }
          });
        }

        // Initialize cookie-settings
        if (!pages['cookie-settings']) {
          await initializePage('cookie-settings', {
            title_fr: 'Paramètres de Cookies',
            sections: {
              banner: {
                title: "Nous utilisons des cookies",
                description: "Ce site utilise des cookies pour améliorer votre expérience de navigation et analyser l'utilisation du site. En continuant à naviguer, vous acceptez notre utilisation des cookies.",
                settings_button: "Paramètres",
                reject_button: "Refuser",
                accept_button: "Accepter tout"
              },
              modal: {
                title: "Paramètres des cookies",
                description: "Vous pouvez personnaliser vos préférences de cookies ci-dessous. Vos choix seront enregistrés et appliqués à toutes les pages du site."
              },
              essential: {
                title: "Cookies essentiels",
                description: "Ces cookies sont nécessaires au fonctionnement du site web et ne peuvent pas être désactivés dans nos systèmes."
              },
              analytics: {
                title: "Cookies analytiques",
                description: "Ces cookies nous permettent de mesurer et d'analyser l'audience de notre site web pour améliorer son fonctionnement."
              },
              marketing: {
                title: "Cookies marketing",
                description: "Ces cookies peuvent être mis en place par nos partenaires publicitaires pour établir un profil de vos intérêts."
              },
              buttons: {
                cancel: "Annuler",
                save: "Enregistrer les préférences"
              }
            }
          });
        }
      }
    };

    initializePages();
  }, [loading, pages, initializePage]);

  return { 
    pages, 
    loading, 
    error, 
    refetch: fetchPages,
    updatePage,
    getPageContent,
    initializePage
  };
};